import { Form, useFormikContext } from 'formik';
import { ArrowUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { HelmRelease } from '@/react/kubernetes/helm/types';
import { HelmValuesInput } from '@/react/kubernetes/helm/components/HelmValuesInput';
import {
  ChartVersion,
  useHelmRepoVersions,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';
import { useHelmChartValues } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartValues';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { HelmVersionSelect } from '@/react/kubernetes/helm/components/HelmVersionSelect';
import { ManifestPreviewFormSection } from '@/react/kubernetes/helm/components/ManifestPreviewFormSection';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { WidgetTitle } from '@@/Widget';
import { Checkbox } from '@@/form-components/Checkbox';
import { Option } from '@@/form-components/PortainerSelect';
import { FormSection } from '@@/form-components/FormSection';

import { UpgradeHelmFormValues } from './types';
import { ChartReferenceFormSection } from './ChartReferenceFormSection';

interface Props {
  release: HelmRelease;
  onDismiss: () => void;
  chartReferenceInfoMessage?: string;
  isCheckingChartReference: boolean;
  initialRegistryValue?: RegistryValue;
  chartReferenceState: {
    requiresSourceConfirmation: boolean;
    requiresChartConfirmation: boolean;
  };
}

export function UpgradeHelmInnerForm({
  release,
  onDismiss,
  chartReferenceInfoMessage,
  isCheckingChartReference,
  initialRegistryValue,
  chartReferenceState,
}: Props) {
  const environmentId = useEnvironmentId();
  const [previewIsValid, setPreviewIsValid] = useState(false);
  const {
    values,
    setFieldValue,
    setValues,
    isSubmitting,
    submitForm,
    errors,
    isValid,
    dirty,
  } = useFormikContext<UpgradeHelmFormValues>();

  const chartValuesRefQuery = useHelmChartValues({
    chart: values.chart,
    repo: values.repo,
    registryId: values.registryId,
    version: values.version,
    environmentId,
  });

  const versionOptionsQuery = useHelmVersionOptions(
    values,
    release.chart.metadata?.version ?? ''
  );
  const versionOptions = versionOptionsQuery.data ?? [];

  const selectedVersion =
    versionOptions.find((v) => v.value.version === values.version)?.value ||
    null;

  useAutoSelectVersion(
    versionOptions,
    (version) => setFieldValue('version', version),
    release.chart.metadata?.version,
    values.chart
  );

  const payload = useMemo(
    () => ({
      name: release.name,
      namespace: release.namespace,
      chart: values.chart,
      version: values.version,
      repo: values.registryId ? undefined : values.repo,
      registryId: values.registryId,
      values: values.values,
    }),
    [
      release.name,
      release.namespace,
      values.chart,
      values.version,
      values.repo,
      values.registryId,
      values.values,
    ]
  );

  return (
    <Form>
      <Modal
        onDismiss={onDismiss}
        size="xl"
        className="flex flex-col h-[80vh] px-0"
        aria-label="upgrade-helm"
      >
        <Modal.Header
          title={
            <WidgetTitle
              className="px-5"
              title={`Edit/Upgrade ${release.name} in namespace '${release.namespace}'`}
              icon={ArrowUp}
            />
          }
        />
        <div className="flex-1 overflow-y-auto px-5">
          <Modal.Body>
            <div className="form-horizontal">
              <ChartReferenceFormSection
                chartReferenceInfoMessage={chartReferenceInfoMessage}
                isCheckingChartReference={isCheckingChartReference}
                formValues={values}
                setValues={setValues}
                formErrors={{
                  registryId: errors.registryId,
                  chart: errors.chart,
                }}
                namespace={release.namespace}
                initialRegistryValue={initialRegistryValue}
                hasVersionOptions={versionOptions.length > 0}
                chartReferenceState={chartReferenceState}
                releaseChartMetadata={release.chart.metadata}
              />
              <FormSection title="Upgrade options">
                <FormControl
                  label="Version"
                  inputId="version-input"
                  size="medium"
                  errors={getNonBlockingVersionErrors(
                    versionOptions,
                    release.chart.metadata?.version ?? '',
                    release.chart.metadata?.appVersion ?? '',
                    values.chart
                  )}
                >
                  <HelmVersionSelect
                    value={selectedVersion}
                    options={versionOptions}
                    onChange={(version) =>
                      setFieldValue('version', version?.version)
                    }
                    isClearable
                    data-cy="helm-version-input"
                    disabled={!values.chart}
                    isLoading={versionOptionsQuery.isInitialLoading}
                    isChartSelected={!!values.chart}
                  />
                </FormControl>
                <FormControl
                  label="Rollback on failure"
                  tooltip="Enables automatic rollback on failure (equivalent to the helm --atomic flag). It may increase the time to upgrade."
                  inputId="atomic-input"
                  size="medium"
                >
                  <Checkbox
                    id="atomic-input"
                    checked={values.atomic}
                    data-cy="atomic-checkbox"
                    onChange={(e) => setFieldValue('atomic', e.target.checked)}
                  />
                </FormControl>
                <HelmValuesInput
                  values={values.values}
                  setValues={(values) => setFieldValue('values', values)}
                  valuesRef={chartValuesRefQuery.data?.values ?? ''}
                  isValuesRefLoading={chartValuesRefQuery.isInitialLoading}
                />
              </FormSection>
              <div className="mb-10">
                <ManifestPreviewFormSection
                  payload={payload}
                  onChangePreviewValidation={setPreviewIsValid}
                  title="Manifest preview"
                  environmentId={environmentId}
                  currentManifest={release.manifest}
                />
              </div>
            </div>
          </Modal.Body>
        </div>
        <div className="px-5 border-solid border-0 border-t border-gray-5 th-dark:border-gray-7 th-highcontrast:border-white">
          <Modal.Footer>
            <Button
              onClick={onDismiss}
              color="secondary"
              key="cancel-button"
              size="medium"
              data-cy="cancel-button-cy"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              key="update-button"
              size="medium"
              onClick={async () => {
                await submitForm();
                onDismiss();
              }}
              disabled={isSubmitting || !isValid || !dirty || !previewIsValid}
              data-cy="update-button-cy"
            >
              Edit/Upgrade
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </Form>
  );
}

function getNonBlockingVersionErrors(
  versionOptions: Option<ChartVersion>[],
  releaseChartVersion: string,
  releaseAppVersion: string,
  chart: string
) {
  return (
    getReleaseVersionValidationMessage(
      versionOptions,
      releaseChartVersion,
      releaseAppVersion
    ) || getChartSelectMessage(chart)
  );
}

/** Give a small non blocking warning if the release version isn't in the options. It's likely that the user has selected the wrong chart. */
function getReleaseVersionValidationMessage(
  versionOptions: Option<ChartVersion>[],
  releaseChartVersion: string,
  releaseAppVersion: string
) {
  // skip validation if no release version or no version options
  if (!releaseChartVersion || versionOptions.length === 0) {
    return '';
  }

  const versionOptionsValidation = versionOptions.reduce(
    (acc, version) => {
      const hasReleaseVersion = version.value.version === releaseChartVersion;
      const hasApplicationVersion =
        version.value.appVersion === releaseAppVersion;
      return {
        hasReleaseAndApplicationVersion:
          acc.hasReleaseAndApplicationVersion ||
          (hasReleaseVersion && hasApplicationVersion),
        hasReleaseVersion: acc.hasReleaseVersion || hasReleaseVersion,
        hasApplicationVersion:
          acc.hasApplicationVersion || hasApplicationVersion,
      };
    },
    {
      hasReleaseAndApplicationVersion: false,
      hasReleaseVersion: false,
      hasApplicationVersion: false,
    }
  );

  if (versionOptionsValidation.hasReleaseAndApplicationVersion) {
    return '';
  }

  if (versionOptionsValidation.hasReleaseVersion) {
    // missing app version
    return `There is no version available with a matching app version '${releaseAppVersion}' for the release. Do you have the right chart selected?`;
  }

  if (versionOptionsValidation.hasApplicationVersion) {
    // missing release version
    return `There is no version available with a matching Helm chart version '${releaseChartVersion}' for the release. Do you have the right chart selected?`;
  }

  return `There is no version available with a matching Helm chart version '${releaseChartVersion}' and app version '${releaseAppVersion}' for the release. Do you have the right chart selected?`;
}

function getChartSelectMessage(chart: string) {
  return chart ? '' : 'Please select a chart to load available versions.';
}

function useHelmVersionOptions(
  values: UpgradeHelmFormValues,
  releaseVersion: string
) {
  return useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      select: (versions) =>
        versions?.map<Option<ChartVersion>>((version) => ({
          label:
            version.version === releaseVersion
              ? `${version.version} (current)`
              : version.version,
          value: version,
        })),
    },
    {
      chartPath: values.chart,
      repoURL: values.repo,
      registryID: values.registryId,
    },
    true
  );
}

function useAutoSelectVersion(
  options: Option<ChartVersion>[],
  setVersionValue: (versionValue: string) => void,
  releaseVersion?: string,
  chartValue?: string
): void {
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // whenever the chart value updates, reset the auto selected version
  useEffect(() => {
    setHasAutoSelected(false);
  }, [chartValue]);

  useEffect(() => {
    if (!hasAutoSelected && options.length > 0 && releaseVersion) {
      if (!releaseVersion) {
        return;
      }
      const matchingVersion = options.find(
        (version) => version.value.version === releaseVersion
      );
      if (matchingVersion) {
        setVersionValue(matchingVersion.value.version);
        setHasAutoSelected(true);
      }
    }
  }, [hasAutoSelected, options, releaseVersion, setVersionValue]);
}
